/**
 * Board-grant REST surface (control/internal/httpapi/board.go, ADR 0013): a moderator/admin
 * grants (POST) or revokes (DELETE) an identity's right to draw on the Доска; the roster
 * converges on the board_grant frame echo. Same-origin /api calls, exactly like
 * join/remint/speak. Unlike speak there is no token-minting route: a grant only flips the
 * client-side editor gate, the media session never changes.
 */

/** Failure of a board endpoint: HTTP error, network failure or malformed response body. */
export class BoardApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'BoardApiError';
    this.status = status;
    this.code = code;
  }
}

/** Body of a successful grant/revoke (board.go boardResponse). */
interface BoardResponse {
  readonly identity: string;
  readonly granted: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Builds the BoardApiError for a non-2xx response from the contract error envelope. */
async function errorFromResponse(response: Response, endpoint: string): Promise<BoardApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // non-JSON error body, keep the generic message
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new BoardApiError(response.status, code, message);
}

/**
 * Shared front of the grant/revoke routes: both answer the boardResponse shape, both are
 * moderator/admin gated server-side (participants get 403 forbidden; a revoke against the
 * owner or a stored admin is refused with 403 too - they always keep the right to draw).
 */
async function boardTargetRequest(
  room: string,
  identity: string,
  method: 'POST' | 'DELETE',
  token: string,
): Promise<BoardResponse> {
  let response: Response;
  try {
    response = await fetch(
      `/api/v1/rooms/${encodeURIComponent(room)}/board/${encodeURIComponent(identity)}`,
      { method, headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    throw new BoardApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, `board/${identity}`);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new BoardApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  if (
    !isRecord(body) ||
    typeof body.identity !== 'string' ||
    body.identity.length === 0 ||
    typeof body.granted !== 'boolean'
  ) {
    throw new BoardApiError(
      response.status,
      'invalid_response',
      'board response violates the board shape',
    );
  }
  return { identity: body.identity, granted: body.granted };
}

/** POST /api/v1/rooms/{room}/board/{identity} (moderator/admin): grant the right to draw. */
export function grantBoard(room: string, identity: string, token: string): Promise<BoardResponse> {
  return boardTargetRequest(room, identity, 'POST', token);
}

/**
 * DELETE /api/v1/rooms/{room}/board/{identity} (moderator/admin): revoke the grant. Revoking
 * an identity without one is an idempotent success; the room owner and stored admins cannot
 * be denied (403).
 */
export function revokeBoard(room: string, identity: string, token: string): Promise<BoardResponse> {
  return boardTargetRequest(room, identity, 'DELETE', token);
}
